import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, getImageUrl } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SecretRevealRow from '../admin/apps/SecretRevealRow';
import CommentThread from './CommentThread';
import StatusTracker from './StatusTracker';
import { toastSuccess, toastError, errorMessage } from '../../lib/toast';
import { formatDateTime } from '../../lib/formatDate';
import './buildApp.css';

// Mandatory, server-injected Google SSO tool — branded with its logo here too.
const SSO_TOOL_NAME = 'Google SSO';
const SSO_TOOL_ICON = '/images/google-sso.png';

// Statuses where tool edits are still permitted.
const PRE_PUBLISH = new Set(['pending', 'approved', 'provisioning', 'active', 'publish_requested']);

const TOOL_EDIT_ERRORS = {
  not_editable: 'This app can no longer be edited.',
  duplicate_tool: 'That tool is already on this app.',
  unknown_tool: 'That tool no longer exists.',
  tools_required: 'An app needs at least one tool.',
  sso_required: "Google SSO is required and can't be removed.",
  forbidden: "You don't have permission to do that.",
  bad_tool: 'Pick a catalog tool or enter a custom name.',
};

const STATUS_LABEL = {
  pending: 'Pending review',
  approved: 'Approved',
  provisioning: 'Provisioning',
  active: 'In progress',
  publish_requested: 'Publish requested',
  published: 'Published',
  rejected: 'Rejected',
};

const TOOL_TYPE_LABEL = {
  tool: 'Tool',
  document: 'Document',
  sheet: 'Sheet',
  service: 'Service',
  dashboard: 'Dashboard',
};

const BUILD_APP_HASH = '/build-app';

function Avatar({ name, photoUrl, className = '' }) {
  if (photoUrl) {
    return <img className={`ba-chip-avatar ${className}`} src={getImageUrl(photoUrl)} alt="" />;
  }
  return (
    <span className={`ba-chip-avatar ba-chip-avatar--fallback ${className}`} aria-hidden="true">
      {(name || '?').charAt(0).toUpperCase()}
    </span>
  );
}

export default function UserAppDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const viewerLabel = (auth && auth.user && auth.user.email) || '';

  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [message, setMessage] = useState(null); // { kind, text }

  // Request-publish form (shown to the creator when the app is in draft/active).
  const [prodUrl, setProdUrl] = useState('');
  const [prodDesc, setProdDesc] = useState('');
  const [prodIcon, setProdIcon] = useState('');
  const [requesting, setRequesting] = useState(false);

  // New comment composer.
  const [commentBody, setCommentBody] = useState('');
  const [posting, setPosting] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const loadComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/app_requests/${id}/comments`, { withCredentials: true });
      if (mountedRef.current) setComments(Array.isArray(res.data) ? res.data : []);
    } catch {
      /* non-fatal — thread just stays as-is */
    }
  }, [id]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/app_requests/${id}/detail`, { withCredentials: true });
      if (mountedRef.current) {
        setDetail(res.data);
        setError(null);
        // Prefill the publish form from any prior production details.
        setProdUrl(res.data.production_url || '');
        setProdDesc(res.data.production_description || '');
        setProdIcon(res.data.production_icon_url || '');
      }
    } catch (err) {
      if (mountedRef.current) {
        const code = err?.response?.status;
        setError(code === 403 ? 'You don’t have access to this app.' : 'Failed to load app.');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); loadComments(); }, [load, loadComments]);

  const requestPublish = async () => {
    setMessage(null);
    // Field-level validation stays inline (shown before the request fires).
    if (!prodUrl.trim()) { setMessage({ kind: 'error', text: 'Production URL is required.' }); return; }
    if (!prodDesc.trim()) { setMessage({ kind: 'error', text: 'Production description is required.' }); return; }
    setRequesting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/app_requests/${id}/request-publish`,
        {
          production_url: prodUrl.trim(),
          production_description: prodDesc.trim(),
          production_icon_url: prodIcon.trim() || null,
        },
        { withCredentials: true });
      if (mountedRef.current) {
        setRequesting(false);
        toastSuccess('Publish requested. An admin will review it.');
        await load();
      }
    } catch (err) {
      if (mountedRef.current) {
        toastError(errorMessage(err, 'Publish request failed.', {
          production_url_required: 'Enter a valid production URL (http or https).',
          'link must be http or https': 'Enter a valid production URL (http or https).',
          production_description_required: 'Production description is required.',
          not_active: 'This app can’t be submitted for publishing right now.',
        }));
        setRequesting(false);
      }
    }
  };

  const decideJoin = async (joinId, decision) => {
    try {
      await axios.post(`${API_BASE_URL}/api/app_requests/${id}/join/${joinId}/decide`,
        { decision }, { withCredentials: true });
      toastSuccess(decision === 'approve' ? 'Added to the team.' : 'Request declined.');
      await load();
    } catch (err) {
      toastError(errorMessage(err, 'Couldn’t update that request. Try again.'));
    }
  };

  // Add-tool row state.
  const [addingTool, setAddingTool] = useState(false);
  const [addMode, setAddMode] = useState('catalog');
  const [catalog, setCatalog] = useState([]);
  const [selTool, setSelTool] = useState('');
  const [customName, setCustomName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const openAddRow = async () => {
    setAddingTool(true);
    if (catalog.length === 0) {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/tools/catalog-for-apps`, { withCredentials: true });
        if (mountedRef.current) setCatalog(Array.isArray(res.data) ? res.data : []);
      } catch {
        /* catalog stays empty — user can still use custom mode */
      }
    }
  };

  const addTool = async () => {
    setBusy(true);
    try {
      const body = addMode === 'catalog'
        ? { tool_id: selTool, usage_note: note }
        : { custom_tool_name: customName, usage_note: note };
      await axios.post(`${API_BASE_URL}/api/app_requests/${id}/tools`, body, { withCredentials: true });
      toastSuccess('Tool added.');
      setAddingTool(false);
      setAddMode('catalog');
      setCustomName('');
      setNote('');
      setSelTool('');
      await load();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't update the tools. Try again.", TOOL_EDIT_ERRORS));
    } finally {
      setBusy(false);
    }
  };

  const removeTool = async (t) => {
    if ((t.secrets || []).length > 0 &&
        !window.confirm('This tool has an assigned secret. Removing it deletes that assignment. Continue?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/app_requests/${id}/tools/${t.app_request_tool_id}`, { withCredentials: true });
      toastSuccess('Tool removed.');
      await load();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't update the tools. Try again.", TOOL_EDIT_ERRORS));
    }
  };

  const [cancelling, setCancelling] = useState(false);
  const cancelRequest = async () => {
    setCancelling(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/app_requests/${id}`, { withCredentials: true });
      toastSuccess('Request cancelled.');
      navigate(BUILD_APP_HASH);
    } catch (err) {
      if (mountedRef.current) {
        toastError(errorMessage(err, 'Couldn’t cancel that request. Try again.', {
          not_pending: 'This request can no longer be cancelled.',
        }));
        setCancelling(false);
      }
    }
  };

  const postComment = async () => {
    if (!commentBody.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/app_requests/${id}/comments`,
        { body: commentBody.trim() }, { withCredentials: true });
      if (mountedRef.current) {
        setComments((prev) => [...prev, res.data]);
        setCommentBody('');
        setPosting(false);
      }
    } catch (err) {
      if (mountedRef.current) { toastError(errorMessage(err, 'Couldn’t post your comment. Try again.')); setPosting(false); }
    }
  };

  const BackButton = (
    <button
      type="button"
      className="btn btn-secondary btn-small ba-back"
      onClick={() => navigate(BUILD_APP_HASH)}
    >
      ← Back to my apps
    </button>
  );

  if (loading) {
    return (
      <div className="main-content-inner">
        <div className="ba-detail" aria-busy="true">
          {BackButton}
          <div className="ba-skeleton ba-skeleton--title" />
          <div className="ba-skeleton ba-skeleton--line" />
          <div className="ba-skeleton ba-skeleton--card" />
          <div className="ba-skeleton ba-skeleton--card" />
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="main-content-inner">
        <div className="ba-detail">
          {BackButton}
          <div className="ba-detail-empty" role="alert">
            <span className="ba-detail-empty-icon" aria-hidden="true">!</span>
            <p>{error || 'App not found.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const collaborators = detail.collaborators || [];
  const tools = detail.tools || [];
  const canReveal = detail.can_reveal === true;
  const hasRefs = tools.some((t) => (t.secrets || []).length > 0);
  const isActiveLike = ['provisioning', 'active', 'publish_requested', 'published'].includes(detail.status);
  const createdAt = formatDateTime(detail.created_at);
  // Any participant (creator OR collaborator) can request publishing from the
  // draft (active) state. The server enforces participant authz; reaching this
  // page already means the viewer is a participant.
  const canRequestPublish = detail.status === 'active';
  // Only the creator can edit or cancel, and only while pending review.
  const canEditOrCancel = detail.is_creator && detail.status === 'pending';
  const isPublishRequested = detail.status === 'publish_requested';
  const isPublished = detail.status === 'published';
  const canEditTools = PRE_PUBLISH.has(detail.status);

  return (
    <div className="main-content-inner">
      <div className="ba-detail">
        {BackButton}

        <header className={`ba-hero ba-hero--${detail.status}`}>
          <div className="ba-hero-top">
            <div className="ba-hero-titles">
              <h1 className="ba-hero-title">{detail.name}</h1>
              {detail.description && <p className="ba-hero-desc">{detail.description}</p>}
            </div>
            <div className="ba-hero-top-right">
              <div className="ba-hero-tags">
                <span className={`ba-badge ba-badge-${detail.status}`}>
                  {STATUS_LABEL[detail.status] || detail.status}
                </span>
                <span className={`ba-role-tag ba-role-tag--${detail.is_creator ? 'owner' : 'collab'}`}>
                  {detail.is_creator ? 'Owner' : 'Collaborator'}
                </span>
              </div>
              {canEditOrCancel && (
                <div className="ba-hero-actions">
                  <button
                    type="button"
                    className="btn btn-secondary btn-small"
                    onClick={() => navigate(`${BUILD_APP_HASH}#edit=${detail.id}`)}
                  >
                    Edit request
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-small"
                    onClick={cancelRequest}
                    disabled={cancelling}
                  >
                    {cancelling ? 'Cancelling…' : 'Cancel request'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="ba-hero-meta">
            <Avatar name={detail.requester_name} photoUrl={detail.requester_photo_url} />
            <span>
              {detail.is_creator ? 'Created by you' : <>Created by <strong>{detail.requester_name}</strong></>}
              {createdAt && <span className="ba-hero-meta-time"> · {createdAt}</span>}
            </span>
          </div>

          <StatusTracker status={detail.status} />
        </header>

        {detail.status === 'rejected' && detail.admin_notes && (
          <div className="ba-detail-banner ba-detail-banner--error" role="status">
            <strong>Reviewer note:</strong> {detail.admin_notes}
          </div>
        )}

        {message && (
          <div className={`ba-detail-banner ba-detail-banner--${message.kind === 'success' ? 'success' : 'error'}`} role="status">
            {message.text}
          </div>
        )}

        <div className="ba-detail-grid">
          <main className="ba-detail-main">
            {isPublished && (
              <section className="ba-detail-section ba-publish-done">
                <h2 className="ba-detail-section-title">🎉 Published to the catalog</h2>
                <p className="ba-muted ba-detail-section-hint">
                  This app is live as a catalog tool{detail.published_at && <> (published {formatDateTime(detail.published_at)})</>}.
                  {detail.production_url && (
                    <> View it at <a href={detail.production_url} target="_blank" rel="noopener noreferrer">{detail.production_url}</a>.</>
                  )}
                </p>
              </section>
            )}

            {isPublishRequested && (
              <section className="ba-detail-section ba-publish-pending">
                <h2 className="ba-detail-section-title">Publish under review</h2>
                <p className="ba-muted ba-detail-section-hint">
                  An admin is reviewing your publish request{detail.publish_requested_at && <> (submitted {formatDateTime(detail.publish_requested_at)})</>}.
                  They’ll publish it to the catalog or leave feedback in the discussion below.
                </p>
                {detail.production_url && (
                  <dl className="ba-facts" style={{ marginTop: 8 }}>
                    <div className="ba-fact"><dt>Production URL</dt><dd><a href={detail.production_url} target="_blank" rel="noopener noreferrer">{detail.production_url}</a></dd></div>
                  </dl>
                )}
              </section>
            )}

            {canRequestPublish && (
              <section className="ba-detail-section" aria-labelledby="ba-publish-heading">
                <h2 className="ba-detail-section-title" id="ba-publish-heading">Ready to ship? Request publish</h2>
                <p className="ba-muted ba-detail-section-hint">
                  When your app is built and live, submit its production URL and a short description for the
                  catalog listing. An admin will review and either publish it or send feedback.
                </p>
                <div className="form-group">
                  <label htmlFor="ba-prod-url">Production URL</label>
                  <input id="ba-prod-url" type="text" value={prodUrl} onChange={(e) => setProdUrl(e.target.value)} placeholder="https://…" />
                </div>
                <div className="form-group">
                  <label htmlFor="ba-prod-desc">Production description</label>
                  <textarea id="ba-prod-desc" rows={3} maxLength={2000} value={prodDesc} onChange={(e) => setProdDesc(e.target.value)} placeholder="What the app does, for the catalog listing." />
                </div>
                <div className="form-group">
                  <label htmlFor="ba-prod-icon">Icon URL (optional)</label>
                  <input id="ba-prod-icon" type="text" value={prodIcon} onChange={(e) => setProdIcon(e.target.value)} placeholder="https://…/icon.png" />
                </div>
                <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                  <button type="button" className="btn btn-primary" onClick={requestPublish} disabled={requesting}>
                    {requesting ? 'Submitting…' : 'Request publish'}
                  </button>
                </div>
              </section>
            )}

            <section className="ba-detail-section" aria-labelledby="ba-tools-heading">
              <div className="ba-detail-section-head">
                <h2 className="ba-detail-section-title" id="ba-tools-heading">Tools &amp; access</h2>
                <span className="ba-detail-section-count">{tools.length}</span>
                {canEditTools && !addingTool && (
                  <button type="button" className="ba-btn ba-btn--ghost" onClick={openAddRow}>
                    Add tool
                  </button>
                )}
              </div>

              {canEditTools && addingTool && (
                <div className="ba-add-tool-row">
                  <div className="ba-add-tool-toggle" role="group" aria-label="Tool type">
                    <button
                      type="button"
                      className={`ba-btn ba-btn--ghost${addMode === 'catalog' ? ' ba-btn--active' : ''}`}
                  aria-pressed={addMode === 'catalog'}
                      onClick={() => setAddMode('catalog')}
                    >
                      Catalog tool
                    </button>
                    <button
                      type="button"
                      className={`ba-btn ba-btn--ghost${addMode === 'custom' ? ' ba-btn--active' : ''}`}
                  aria-pressed={addMode === 'custom'}
                      onClick={() => setAddMode('custom')}
                    >
                      Custom tool
                    </button>
                  </div>
                  {addMode === 'catalog' ? (
                    <div className="form-group">
                      <label htmlFor="ba-add-tool-select">Catalog tool</label>
                      <select id="ba-add-tool-select" value={selTool} onChange={(e) => setSelTool(e.target.value)}>
                        <option value="">— pick a tool —</option>
                        {catalog.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label htmlFor="ba-add-tool-custom">Tool name</label>
                      <input
                        id="ba-add-tool-custom"
                        type="text"
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        placeholder="e.g. Internal CRM"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="ba-add-tool-note">How it&apos;s used (optional)</label>
                    <textarea
                      id="ba-add-tool-note"
                      rows={2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Describe how your app will use this tool."
                    />
                  </div>
                  <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                    <button type="button" className="btn btn-primary btn-small" onClick={addTool} disabled={busy}>
                      {busy ? 'Adding…' : 'Add'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={() => { setAddingTool(false); setCustomName(''); setNote(''); setSelTool(''); setAddMode('catalog'); }}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {isActiveLike && hasRefs && (
                <p className="ba-muted ba-detail-section-hint">
                  {canReveal
                    ? 'References point to AWS Parameter Store. Use the eye to reveal a value on demand — values are shown transiently, never stored, and every reveal is audit-logged.'
                    : 'References point to AWS Parameter Store. Only the app creator can reveal values.'}
                </p>
              )}

              {tools.length === 0 ? (
                <p className="ba-muted">No tools on this request.</p>
              ) : (
                <div className="ba-detail-tools">
                  {tools.map((t) => {
                    const secrets = (t.secrets || []).filter((s) => s.assignment_kind === 'ssm');
                    const datasets = (t.secrets || []).filter((s) => s.assignment_kind === 'warehouse');
                    const isCustom = t.is_custom === true || (t.tool_id == null && !t.tool_type);
                    const isSso = isCustom && (t.tool_name || '').toLowerCase() === SSO_TOOL_NAME.toLowerCase();
                    const type = t.tool_type || 'tool';
                    return (
                      <article className={`ba-detail-tool${isSso ? ' ba-detail-tool--sso' : ''}`} key={t.app_request_tool_id}>
                        <div className="ba-detail-tool-head">
                          <span className="ba-detail-tool-icon" aria-hidden="true">
                            {isSso
                              ? <img src={getImageUrl(SSO_TOOL_ICON)} alt="" />
                              : !isCustom && t.tool_icon_url
                                ? <img src={getImageUrl(t.tool_icon_url)} alt="" />
                                : (t.tool_name || '?').charAt(0).toUpperCase()}
                          </span>
                          <span className="ba-detail-tool-name">{t.tool_name}</span>
                          {isSso ? (
                            <span className="tool-type-badge tool-type-badge--sso">Required</span>
                          ) : isCustom ? (
                            <span className="tool-type-badge tool-type-badge--custom">Custom</span>
                          ) : (
                            <span className={`tool-type-badge tool-type-badge--${type}`}>
                              {TOOL_TYPE_LABEL[type] || 'Tool'}
                            </span>
                          )}
                          {canEditTools && !isSso && (
                            <button
                              type="button"
                              className="ba-btn ba-btn--ghost ba-tool-remove"
                              aria-label={`Remove ${t.tool_name}`}
                              onClick={() => removeTool(t)}
                            >
                              ×
                            </button>
                          )}
                        </div>

                        {t.usage_note && (
                          <p className="ba-detail-tool-note">
                            <span className="ba-detail-tool-note-label">How it&apos;s used</span>
                            {t.usage_note}
                          </p>
                        )}

                        {isActiveLike && (secrets.length > 0 || datasets.length > 0) && (
                          <div className="apps-refs">
                            {/* A tool gets EITHER a single SSM secret OR one-or-more
                                data connections — never a numbered "secrets" list. */}
                            {secrets.length > 0 && (
                              <RefGroup
                                label={secrets.length > 1 ? 'Secrets' : 'Secret'}
                                items={secrets}
                                appId={id}
                                viewerLabel={viewerLabel}
                                canReveal={canReveal}
                              />
                            )}
                            {datasets.length > 0 && (
                              <RefGroup
                                label={datasets.length > 1 ? 'Data connections' : 'Data connection'}
                                items={datasets}
                                appId={id}
                                viewerLabel={viewerLabel}
                                canReveal={canReveal}
                              />
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="ba-detail-section" aria-labelledby="ba-discussion-heading">
              <div className="ba-detail-section-head">
                <h2 className="ba-detail-section-title" id="ba-discussion-heading">Discussion</h2>
                {comments.length > 0 && <span className="ba-detail-section-count">{comments.length}</span>}
              </div>
              <CommentThread
                comments={comments}
                body={commentBody}
                setBody={setCommentBody}
                onPost={postComment}
                posting={posting}
              />
            </section>
          </main>

          <aside className="ba-detail-side">
            <section className="ba-detail-section" aria-labelledby="ba-team-heading">
              <div className="ba-detail-section-head">
                <h2 className="ba-detail-section-title" id="ba-team-heading">Team</h2>
                {collaborators.length > 0 && <span className="ba-detail-section-count">{collaborators.length}</span>}
              </div>
              {collaborators.length === 0 ? (
                <p className="ba-muted">No collaborators.</p>
              ) : (
                <ul className="ba-side-people">
                  {collaborators.map((c) => (
                    <li key={c.user_id} className="ba-side-person">
                      <Avatar name={c.name} photoUrl={c.photo_url} />
                      <span className="ba-side-person-text">
                        <span className="ba-side-person-name">{c.name}</span>
                        {c.email && <span className="ba-side-person-email">{c.email}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {detail.is_creator && Array.isArray(detail.join_requests) && detail.join_requests.length > 0 && (
              <section className="ba-joinreq">
                <h2 className="ba-section-title">Join requests</h2>
                <ul className="ba-joinreq-list">
                  {detail.join_requests.map((j) => (
                    <li key={j.id} className="ba-joinreq-row">
                      <div className="ba-joinreq-who">
                        {j.photo_url ? <img src={j.photo_url} alt="" className="ba-joinreq-avatar" /> : null}
                        <div>
                          <strong>{j.name}</strong>
                          {j.message ? <p className="ba-joinreq-msg">{j.message}</p> : null}
                        </div>
                      </div>
                      <div className="ba-joinreq-actions">
                        <button type="button" className="btn btn-primary btn-small" onClick={() => decideJoin(j.id, 'approve')}>Approve</button>
                        <button type="button" className="btn btn-secondary btn-small" onClick={() => decideJoin(j.id, 'decline')}>Decline</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="ba-detail-section" aria-labelledby="ba-facts-heading">
              <h2 className="ba-detail-section-title" id="ba-facts-heading">At a glance</h2>
              <dl className="ba-facts">
                <div className="ba-fact">
                  <dt>Status</dt>
                  <dd><span className={`ba-badge ba-badge-${detail.status}`}>{STATUS_LABEL[detail.status] || detail.status}</span></dd>
                </div>
                <div className="ba-fact">
                  <dt>Tools</dt>
                  <dd>{tools.length}</dd>
                </div>
                <div className="ba-fact">
                  <dt>Your role</dt>
                  <dd>{detail.is_creator ? 'Creator' : 'Collaborator'}</dd>
                </div>
                {createdAt && (
                  <div className="ba-fact">
                    <dt>Created</dt>
                    <dd>{createdAt}</dd>
                  </div>
                )}
              </dl>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}

function RefGroup({ label, items, appId, viewerLabel, canReveal }) {
  return (
    <div className="apps-ref-group">
      <strong className="apps-ref-label">{label}</strong>
      <ul className="apps-ref-list">
        {items.map((s, i) => (
          <SecretRevealRow
            key={s.id}
            secret={s}
            index={i}
            total={items.length}
            appId={appId}
            viewerLabel={viewerLabel}
            canReveal={canReveal}
            hideName
          />
        ))}
      </ul>
    </div>
  );
}
